/**
 * @file react-day-pickerをベースにしたカレンダーUIコンポーネント。
 *
 * プロジェクトのデザイントークンに合わせてスタイルがカスタマイズされています。
 * `DayPicker`のラッパーとして機能し、ドロップダウンによる年月選択や
 * カスタムアイコンなどの追加機能を提供します。
 */
"use client";

import {
  addMonths,
  endOfYear,
  setMonth,
  setYear,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import * as React from "react";
import {
  DayButton,
  DayPicker,
  Dropdown,
  getDefaultClassNames,
  MonthCaption,
  useDayPicker,
} from "react-day-picker";

import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * 現在描画中の月キャプションの情報。
 *
 * `numberOfMonths` が 2 以上のとき、react-day-picker のドロップダウンは
 * 「表示中の先頭月」を移動させる前提で作られており、2 つ目以降のキャプションで
 * 月・年を選んでも、その位置に選んだ月が表示されない。
 * オフセット補正のために、キャプション側から表示位置と、その位置に
 * 表示できる月の範囲を伝える。
 */
const CalendarCaptionContext = React.createContext<{
  displayIndex: number;
  captionDate: Date;
  /** このキャプション位置に表示できる最小の月（この月を含む）。`undefined` は下限なし。 */
  minCaptionMonth: Date | undefined;
  /** このキャプション位置に表示できる最大の月（この月を含む）。`undefined` は上限なし。 */
  maxCaptionMonth: Date | undefined;
} | null>(null);

/**
 * プロジェクト用にスタイリングされたカレンダーコンポーネント。
 * `react-day-picker`の`DayPicker`をラップし、独自のスタイルとコンポーネントを適用します。
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  lang,
  fromDate,
  toDate,
  startMonth,
  endMonth,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();
  const [navStart, navEnd] = useNavMonths({
    captionLayout,
    startMonth,
    endMonth,
    today: props.today,
  });

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fromDate={fromDate}
      toDate={toDate}
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      lang={lang}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(lang || "default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "flex gap-4 flex-col md:flex-row relative",
          defaultClassNames.months,
        ),
        month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
        nav: cn(
          "flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5",
          lang === "ja" && "flex-row-reverse",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          "absolute bg-popover inset-0 opacity-0",
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-sm"
            : "rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5",
          defaultClassNames.caption_label,
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none",
          defaultClassNames.weekday,
        ),
        week: cn(
          "flex w-full mt-2 justify-start min-h-[var(--cell-size)]",
          defaultClassNames.week,
        ),
        week_number_header: cn(
          "select-none w-(--cell-size)",
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          "text-[0.8rem] select-none text-muted-foreground",
          defaultClassNames.week_number,
        ),
        day: cn(
          "relative w-[var(--cell-size)] h-[var(--cell-size)] p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none flex-shrink-0",
          defaultClassNames.day,
        ),
        range_start: cn(
          "rounded-l-md bg-accent",
          defaultClassNames.range_start,
        ),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today,
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside,
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled,
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        MonthCaption: ({ calendarMonth, displayIndex, ...props }) => (
          <CalendarCaptionContext.Provider
            value={{
              displayIndex,
              captionDate: calendarMonth.date,
              // 先頭月は navStart より手前へ移動できないため、displayIndex 位置の
              // キャプションに表示できる最小の月は navStart の displayIndex か月後になる。
              minCaptionMonth: navStart && addMonths(navStart, displayIndex),
              maxCaptionMonth: navEnd,
            }}
          >
            <MonthCaption
              calendarMonth={calendarMonth}
              displayIndex={displayIndex}
              {...props}
            />
          </CalendarCaptionContext.Provider>
        ),
        MonthsDropdown: (props) => (
          <CalendarOffsetDropdown {...props} unit="month" />
        ),
        YearsDropdown: (props) => (
          <CalendarOffsetDropdown {...props} unit="year" />
        ),
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

/**
 * react-day-picker が内部で持つナビゲーション可能な月の範囲を再現する。
 *
 * この範囲は context から取得できないが、オフセット補正時のクランプ判定に必要。
 * `startMonth` / `endMonth` 未指定時の既定値は react-day-picker の `getNavMonths`
 * と同じで、年ドロップダウンを表示する場合のみ既定の範囲を持つ。
 */
function useNavMonths({
  captionLayout,
  startMonth,
  endMonth,
  today,
}: Pick<
  React.ComponentProps<typeof DayPicker>,
  "captionLayout" | "startMonth" | "endMonth" | "today"
>): [start: Date | undefined, end: Date | undefined] {
  const startTime = startMonth?.getTime();
  const endTime = endMonth?.getTime();
  const todayTime = today?.getTime();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Date は getTime() の値で比較する
  return React.useMemo(() => {
    const hasYearDropdown =
      captionLayout === "dropdown" || captionLayout === "dropdown-years";
    const baseDate = today ?? new Date();

    return [
      startMonth
        ? startOfMonth(startMonth)
        : hasYearDropdown
          ? startOfYear(subYears(baseDate, 100))
          : undefined,
      endMonth
        ? startOfMonth(endMonth)
        : hasYearDropdown
          ? startOfMonth(endOfYear(baseDate))
          : undefined,
    ];
  }, [captionLayout, startTime, endTime, todayTime]);
}

/** `month` を `[min, max]` の範囲に収める。`undefined` の端は無制限として扱う。 */
function clampMonth(
  month: Date,
  min: Date | undefined,
  max: Date | undefined,
): Date {
  if (min && month < min) return min;
  if (max && month > max) return max;
  return month;
}

/**
 * 表示位置のオフセットを考慮した月・年ドロップダウン。
 *
 * 2 つ目以降のキャプション（`displayIndex > 0`）では、選択された月・年が
 * そのキャプションに表示されるように、先頭月を `displayIndex` か月分だけ
 * 手前へ移動させる。先頭キャプションでは react-day-picker の既定動作を使う。
 *
 * ナビゲーション範囲の下端では先頭月をそれ以上手前へ動かせず、`goToMonth` が
 * 範囲内へクランプして「選んだ月が別のキャプションに表示される」ため、その位置に
 * 表示できない選択肢は無効化する。年は、同じ年に表示できる月が残っていれば
 * 表示可能な最寄りの月へ寄せ、選択した年自体は尊重する。
 */
function CalendarOffsetDropdown({
  unit,
  options,
  ...props
}: React.ComponentProps<typeof Dropdown> & { unit: "month" | "year" }) {
  const caption = React.useContext(CalendarCaptionContext);
  const { goToMonth } = useDayPicker();

  if (!caption || caption.displayIndex === 0) {
    return <Dropdown {...props} options={options} />;
  }

  const { displayIndex, captionDate, minCaptionMonth, maxCaptionMonth } =
    caption;
  const toCaptionMonth = (value: number) =>
    unit === "month"
      ? setMonth(startOfMonth(captionDate), value)
      : setYear(startOfMonth(captionDate), value);

  /** 選択肢がこのキャプション位置に表示できる月をひとつも持たないか。 */
  const isUnreachable = (value: number) => {
    const candidate = toCaptionMonth(value);
    // 月は選んだ月そのもの、年はその年に含まれる月のいずれかが表示できれば選択できる
    const [rangeStart, rangeEnd] =
      unit === "month"
        ? [candidate, candidate]
        : [startOfYear(candidate), startOfMonth(endOfYear(candidate))];

    return Boolean(
      (minCaptionMonth && rangeEnd < minCaptionMonth) ||
        (maxCaptionMonth && rangeStart > maxCaptionMonth),
    );
  };

  const offsetOptions = options?.map((option) =>
    !option.disabled && isUnreachable(option.value)
      ? { ...option, disabled: true }
      : option,
  );

  const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (event) => {
    const nextCaptionMonth = clampMonth(
      toCaptionMonth(Number(event.target.value)),
      minCaptionMonth,
      maxCaptionMonth,
    );
    goToMonth(subMonths(nextCaptionMonth, displayIndex));
  };

  return (
    <Dropdown {...props} options={offsetOptions} onChange={handleChange} />
  );
}

/**
 * カレンダー内の各日付を表すボタンコンポーネント。
 *
 * `react-day-picker`の`DayButton`をラップし、選択状態や範囲選択に応じて
 * 適切なスタイルを適用します。フォーカス管理も行います。
 */
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar };
